import React from "react";
import { InlineMath } from "react-katex";
import { KeyTerm, ImportantNote, Mnemonic } from "../note-components";
import type { UnitNotes } from "../types";

const cspUnit1Notes: UnitNotes = {
  unitId: "unit-1",
  unitName: "Unit 1: Creative Development",
  examWeight: "10–15%",
  focus: "Collaboration, Program Design, and the Iterative Development Process",
  sections: [
    {
      id: "topic-1-1",
      title: "Topic 1.1: Collaboration",
      content: (
        <div className="space-y-6">
          <section>
            <h3 className="text-xl font-bold text-slate-800 border-b pb-2 mb-4">🤝 The Importance of Collaboration</h3>
            <p className="text-slate-700 leading-relaxed mb-4">
              Collaboration in computer science is the application of diverse perspectives to create better solutions and ensure that computing innovations are effective for all users.
            </p>
            <div className="grid md:grid-cols-2 gap-4 mb-4">
              <div className="bg-gradient-to-br from-indigo-50 to-blue-50 p-4 rounded-lg border border-indigo-200">
                <h4 className="font-bold text-indigo-900">🌍 Avoiding Bias</h4>
                <p className="text-sm text-slate-700 mt-1">
                  Computing innovations reflect their creators. If a team is homogenous, software may contain <strong>bias</strong> (e.g., facial recognition failing to recognize darker skin tones).
                </p>
              </div>
              <div className="bg-gradient-to-br from-emerald-50 to-teal-50 p-4 rounded-lg border border-emerald-200">
                <h4 className="font-bold text-emerald-900">💡 Diverse Perspectives</h4>
                <p className="text-sm text-slate-700 mt-1">
                  Teams with diverse backgrounds identify needs and potential pitfalls that a single individual might miss, leading to more inclusive innovations.
                </p>
              </div>
            </div>
          </section>

          <section>
            <h3 className="text-xl font-bold text-slate-800 border-b pb-2 mb-4">🚀 Computing Innovations</h3>
            <p className="text-slate-700 mb-4">An innovation that includes a computer or program code as an integral part of its functionality.</p>
            <div className="space-y-3">
              <div className="flex gap-3">
                <div className="bg-slate-100 p-3 rounded-lg border border-slate-200 flex-1">
                  <h4 className="font-bold text-slate-800 text-sm">🛠️ Physical</h4>
                  <p className="text-xs text-slate-600">Self-driving cars, 3D printers, smart appliances.</p>
                </div>
                <div className="bg-slate-100 p-3 rounded-lg border border-slate-200 flex-1">
                  <h4 className="font-bold text-slate-800 text-sm">📱 Non-Physical (Software)</h4>
                  <p className="text-xs text-slate-600">Picture editing software, mobile apps, video games.</p>
                </div>
                <div className="bg-slate-100 p-3 rounded-lg border border-slate-200 flex-1">
                  <h4 className="font-bold text-slate-800 text-sm">🌐 Conceptual</h4>
                  <p className="text-xs text-slate-600">E-commerce, social networking.</p>
                </div>
              </div>
            </div>
          </section>

          <section>
            <h3 className="text-xl font-bold text-slate-800 border-b pb-2 mb-4">⌨️ Models of Collaboration</h3>
            <div className="bg-slate-50 p-4 rounded-lg border border-slate-200 mb-4">
              <h4 className="font-bold text-slate-900 mb-2">Pair Programming</h4>
              <p className="text-sm text-slate-700 mb-3">Two programmers work at one workstation, switching roles frequently:</p>
              <ul className="space-y-2">
                <li className="text-sm text-slate-700"><strong>The Driver:</strong> Controls the keyboard and mouse; writes the code.</li>
                <li className="text-sm text-slate-700"><strong>The Navigator:</strong> Reviews code, looks for errors, and thinks strategically about the big picture.</li>
              </ul>
            </div>
            <div className="grid md:grid-cols-2 gap-3">
              <KeyTerm term="Online Documents" definition="Allows for asynchronous contribution (e.g., Google Drive)." />
              <KeyTerm term="Version Control" definition="Systems like Git allow multiple programmers to work on the same codebase without overwriting work." />
            </div>
          </section>
        </div>
      )
    },
    {
      id: "topic-1-2",
      title: "Topic 1.2: Program Function and Purpose",
      content: (
        <div className="space-y-6">
          <section>
            <h3 className="text-xl font-bold text-slate-800 border-b pb-2 mb-4">🎯 Function vs. Purpose</h3>
            <div className="grid md:grid-cols-2 gap-4 mb-4">
              <div className="bg-purple-50 p-4 rounded-lg border border-purple-200">
                <h4 className="font-bold text-purple-900">Purpose (The "Why")</h4>
                <p className="text-sm text-slate-700 mt-1 italic">"The purpose is to help students study for biology tests."</p>
                <p className="text-xs text-slate-500 mt-2">The goal or objective the developer is trying to solve.</p>
              </div>
              <div className="bg-blue-50 p-4 rounded-lg border border-blue-200">
                <h4 className="font-bold text-blue-900">Function (The "What")</h4>
                <p className="text-sm text-slate-700 mt-1 italic">"When the user clicks a button, the screen displays a definition."</p>
                <p className="text-xs text-slate-500 mt-2">How the program behaves; what the user sees and does.</p>
              </div>
            </div>
          </section>

          <section>
            <h3 className="text-xl font-bold text-slate-800 border-b pb-2 mb-4">⚙️ Program Components</h3>
            <div className="space-y-3">
              <KeyTerm 
                term="Input" 
                definition="Data sent to a computer (Tactile, Audio, Visual, Text) from sources like keyboards, sensors (GPS, Temp), or files."
              />
              <KeyTerm 
                term="Output" 
                definition="Data sent back from the program to the device (On-screen graphics, audio sounds, or tactile vibrations)."
              />
              <KeyTerm 
                term="Processing" 
                definition="The logic (code) that turns inputs into outputs."
              />
            </div>
          </section>

          <section>
            <h3 className="text-xl font-bold text-slate-800 border-b pb-2 mb-4">🖱️ Event-Driven Programming</h3>
            <p className="text-slate-700 mb-4">Programs wait for an <strong>Event</strong> (an action like clicking a button or a timer finishing) to trigger an <strong>Event Handler</strong> (the chunk of code that runs in response).</p>
            <ImportantNote>
              In event-driven programming, the flow of execution is determined by the <strong>user's timing</strong>, not the programmer's predetermined sequence.
            </ImportantNote>
          </section>
        </div>
      )
    },
    {
      id: "topic-1-3",
      title: "Topic 1.3: Program Design and Development",
      content: (
        <div className="space-y-6">
          <section>
            <h3 className="text-xl font-bold text-slate-800 border-b pb-2 mb-4">🔄 The Development Process</h3>
            <div className="grid md:grid-cols-2 gap-3 mb-4">
              <div className="bg-teal-50 p-4 rounded-lg border border-teal-200">
                <h4 className="font-bold text-teal-900">Iterative Process</h4>
                <p className="text-sm text-slate-700 mt-1">Repeating cycles (Design $\rightarrow$ Code $\rightarrow$ Test) to refine and improve the program.</p>
              </div>
              <div className="bg-cyan-50 p-4 rounded-lg border border-cyan-200">
                <h4 className="font-bold text-cyan-900">Incremental Process</h4>
                <p className="text-sm text-slate-700 mt-1">Breaking a large problem into smaller modules and coding one piece at a time.</p>
              </div>
            </div>
          </section>

          <section>
            <h3 className="text-xl font-bold text-slate-800 border-b pb-2 mb-4">📋 Phases of Development</h3>
            <div className="space-y-3">
              <div className="flex items-start gap-3 p-3 bg-white rounded-lg border border-slate-200">
                <span className="font-bold text-slate-400">1.</span>
                <p className="text-sm text-slate-700"><strong>Investigation & Reflection:</strong> Interviewing users and identifying the problem.</p>
              </div>
              <div className="flex items-start gap-3 p-3 bg-white rounded-lg border border-slate-200">
                <span className="font-bold text-slate-400">2.</span>
                <p className="text-sm text-slate-700"><strong>Design:</strong> Planning logic using <strong>Storyboarding</strong> (visuals), <strong>Pseudocode</strong> (English-like logic), or <strong>Flowcharts</strong>.</p>
              </div>
              <div className="flex items-start gap-3 p-3 bg-white rounded-lg border border-slate-200">
                <span className="font-bold text-slate-400">3.</span>
                <p className="text-sm text-slate-700"><strong>Prototyping:</strong> Building a quick, incomplete version to test ideas.</p>
              </div>
              <div className="flex items-start gap-3 p-3 bg-white rounded-lg border border-slate-200">
                <span className="font-bold text-slate-400">4.</span>
                <p className="text-sm text-slate-700"><strong>Testing:</strong> Running the program to find errors.</p>
              </div>
            </div>
          </section>

          <section>
            <h3 className="text-xl font-bold text-slate-800 border-b pb-2 mb-4">📝 Program Documentation</h3>
            <div className="space-y-3">
              <div className="bg-slate-50 p-4 rounded-lg border border-slate-200">
                <h4 className="font-bold text-slate-900">Comments</h4>
                <p className="text-sm text-slate-700 mt-1">Lines of text read by humans but <strong>ignored by the computer</strong>. Essential for team understanding and explaining "why" code exists.</p>
              </div>
              <KeyTerm 
                term="Attribution" 
                definition="Acknowledging the owner of code libraries, images, or logic (Open Source) used in your program. Failure to do so is plagiarism."
              />
            </div>
          </section>
        </div>
      )
    },
    {
      id: "topic-1-4",
      title: "Topic 1.4: Identifying and Correcting Errors",
      content: (
        <div className="space-y-6">
          <section>
            <h3 className="text-xl font-bold text-slate-800 border-b pb-2 mb-4">⚠️ Types of Errors</h3>
            <div className="space-y-3">
              <div className="bg-red-50 p-4 rounded-lg border-l-4 border-red-400">
                <h4 className="font-bold text-red-900">Syntax Error</h4>
                <p className="text-sm text-slate-700">Grammar/punctuation rules are broken (e.g., missing <code className="bg-red-100 px-1">;</code> or typo like <code className="bg-red-100 px-1">pritn</code>). Program <strong>will not run</strong>.</p>
              </div>
              <div className="bg-orange-50 p-4 rounded-lg border-l-4 border-orange-400">
                <h4 className="font-bold text-orange-900">Runtime Error</h4>
                <p className="text-sm text-slate-700">Program starts but <strong>crashes</strong> during execution (e.g., dividing by zero or Index Out of Bounds).</p>
              </div>
              <div className="bg-amber-50 p-4 rounded-lg border-l-4 border-amber-400">
                <h4 className="font-bold text-amber-900">Logic Error</h4>
                <p className="text-sm text-slate-700">Program runs but produces <strong>wrong results</strong> (e.g., using <InlineMath math=">" /> when you meant <InlineMath math="\geq" />). Hardest to detect.</p>
              </div>
              <div className="bg-rose-50 p-4 rounded-lg border-l-4 border-rose-400">
                <h4 className="font-bold text-rose-900">Overflow Error</h4>
                <p className="text-sm text-slate-700">Occurs when a number is outside the defined range (e.g., storing 256 in a system that only counts to 255).</p>
              </div>
            </div>
          </section>

          <section>
            <h3 className="text-xl font-bold text-slate-800 border-b pb-2 mb-4">🛠️ Debugging and Testing</h3>
            <div className="space-y-4">
              <div className="bg-white p-4 rounded-lg border border-slate-200">
                <h4 className="font-bold text-slate-800 text-sm mb-2">Test Cases & Boundary Cases</h4>
                <p className="text-sm text-slate-700">Test with various inputs. For logic like <code className="bg-slate-100 px-1 text-blue-600">IF (score &gt; 10)</code>, test 9, 11, AND the <strong>edge case</strong> 10.</p>
              </div>
              <div className="grid md:grid-cols-2 gap-3">
                <KeyTerm term="Hand Tracing" definition="Manually tracking variable values line-by-line on paper." />
                <KeyTerm term="Output Statements" definition="Adding temporary print() statements to check variable values at specific moments." />
              </div>
              <p className="text-sm text-slate-600"><strong>Visualizations:</strong> Using graphs or logs to see data patterns while the program runs.</p>
            </div>
          </section>

          <section>
            <h3 className="text-xl font-bold text-slate-800 border-b pb-2 mb-4">🎓 Unit 1 Exam Tips</h3>
            <div className="space-y-3">
              <Mnemonic 
                title="Iterative vs. Incremental" 
                content="Iterative = refining through repetition (better); Incremental = building piece by piece (bigger)." 
              />
              <div className="bg-yellow-50 p-4 rounded-lg border border-yellow-200">
                <h4 className="font-bold text-yellow-900 text-sm">Create Task Alignment</h4>
                <ul className="list-disc pl-5 space-y-1 text-xs text-slate-700 mt-2">
                  <li>Distinguish <strong>Purpose</strong> from <strong>Function</strong> in your written response.</li>
                  <li>Document your <strong>Iterative Development Process</strong> (how you identified and fixed errors).</li>
                  <li>Prioritize <strong>User Experience (UI/UX)</strong> and accessibility over programmer convenience.</li>
                </ul>
              </div>
            </div>
          </section>
        </div>
      )
    }
  ]
};

export default cspUnit1Notes;